import { useContext, useEffect } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';

import useTokenContext from 'hooks/useTokenContext';
import useWalletContext from './useWalletContext';
import { CheckoutModalContext } from 'pages/context';

export const useWalletConnect = () => {
  const { isConnected } = useWalletContext();
  const { openModal } = useContext(CheckoutModalContext);
  const { isAuthenticated } = useTokenContext();

  const [callback, setCallback] = useStateIfMounted(() => {});

  const verifyWalletOwnership = (passedCallback) => {
    if (!isAuthenticated) {
      openModal(
        {
          callback: passedCallback || callback,
        },
        'verify_wallet_ownership',
        'sign',
      );
    } else {
      // Execute the callback and clear it
      const callbackToExecute = passedCallback || callback;
      if (callbackToExecute) {
        callbackToExecute();
        setCallback(null);
      }
    }
  };

  const connectWallet = async (callbackParam) => {
    try {
      // Clear any previous callback first
      setCallback(null);
      // Set the new callback
      setCallback(callbackParam);

      if (!isConnected) {
        openModal({ metadata: {} }, 'select_wallet', 'sign');
      } else if (!isAuthenticated) {
        // If connected but not authenticated, verify ownership
        verifyWalletOwnership(callbackParam);
      } else if (callbackParam) {
        // If connected and authenticated, execute callback immediately
        callbackParam();
        // Clear callback after execution
        setCallback(null);
      }
    } catch (err) {
      console.error(err);
      // Clear callback on error
      setCallback(null);
    }
  };

  // Auto-continue the connection flow when wallet connects and we have a pending callback
  // Note: This is now handled by WalletConnectors automatically triggering verification
  // after connection, so this useEffect is less critical but kept for edge cases
  useEffect(() => {
    if (isConnected && !isAuthenticated && callback) {
      // Wallet just connected but not authenticated, continue with verification
      verifyWalletOwnership(callback);
    }
  }, [isConnected, isAuthenticated, callback]);

  // Clear callback when wallet disconnects
  useEffect(() => {
    if (!isConnected && callback) {
      setCallback(null);
    }
  }, [isConnected, callback]);

  return { connectWallet, verifyWalletOwnership };
};
